import { useCallback, useEffect, useRef, useState } from "react";
import { useUrlQuery } from "../../../utils/utils";
import { compressPhoto } from "../../../utils/compress-image";
import {
  submitDamageReport,
  getDamageReport,
  deleteDamagePhoto,
  getSelfcheckinPhotos,
  submitSelfcheckinPhoto,
  deleteSelfcheckinPhoto,
  validateReturnKeylessVehicleQRCode,
  keylessCheckFuelLevelAndKms,
  validateKeylessDropOffLocation,
  preCloseRA,
  getReturnKeylessSession,
  updateReturnStep,
} from "../keylessService";
import AlertModalManager from "../../../components/alert-modal-wraper/alert-modal-manager";

export const STEPS = {
  START: "START",
  VEHICLE_LOCATION: "VEHICLE_LOCATION",
  PARK_DIRECTIONS: "PARK_DIRECTIONS",
  FUEL_LEVEL: "FUEL_LEVEL",
  VALIDATE_FUEL_LEVEL_AND_KMS: "VALIDATE_FUEL_LEVEL_AND_KMS",
  CHECKLIST: "CHECKLIST",
  KEY_DROP_OFF: "KEY_DROP_OFF",
  READ_QR_CODE: "READ_QR_CODE",
  SCAN_QR_CODE: "SCAN_QR_CODE",
  FAIL_QR_CODE: "FAIL_QR_CODE",
  RENTAL_FINAL_STAGE: "RENTAL_FINAL_STAGE",
  WARNING_RENTAL: "WARNING_RENTAL",
  FINISHED_RENTAL: "FINISHED_RENTAL",
  VEHICLE_DAMAGE_REPORT_STEP_1: "VEHICLE_DAMAGE_REPORT_STEP_1",
  VEHICLE_DAMAGE_REPORT_STEP_2: "VEHICLE_DAMAGE_REPORT_STEP_2",
  VEHICLE_DAMAGE_REPORT_STEP_3: "VEHICLE_DAMAGE_REPORT_STEP_3",
  VEHICLE_DAMAGE_REPORT_STEP_4: "VEHICLE_DAMAGE_REPORT_STEP_4",
  SELFCHECKIN_PHOTO: "SELFCHECKIN_PHOTO",
  SELFCHECKIN_CAMERA: "SELFCHECKIN_CAMERA",
};

export const FuelType = Object.freeze({
  ELECTRIC: "electric",
  COMBUSTION: "combustion",
  HYBRID: "hybrid",
});

// Mapa de step da BD → step do frontend
const STEP_FROM_DB = {
  PENDING: STEPS.START,
  VEHICLE_LOCATION: STEPS.VEHICLE_LOCATION,
  FUEL_LEVEL: STEPS.FUEL_LEVEL,
  VALIDATE_FUEL_LEVEL_AND_KMS: STEPS.VALIDATE_FUEL_LEVEL_AND_KMS,
  CHECKLIST: STEPS.CHECKLIST,
  VEHICLE_DAMAGE_REPORT_STEP_1: STEPS.VEHICLE_DAMAGE_REPORT_STEP_1,
  VEHICLE_DAMAGE_REPORT_STEP_2: STEPS.VEHICLE_DAMAGE_REPORT_STEP_2,
  KEY_DROP_OFF: STEPS.KEY_DROP_OFF,
  READ_QR_CODE: STEPS.READ_QR_CODE,
  FAIL_QR_CODE: STEPS.FAIL_QR_CODE,
  DONE: STEPS.FINISHED_RENTAL,
};

// Mapa de step do frontend → step da BD (apenas steps persistíveis)
const STEP_TO_DB = {
  [STEPS.VEHICLE_LOCATION]: "VEHICLE_LOCATION",
  [STEPS.FUEL_LEVEL]: "FUEL_LEVEL",
  [STEPS.VALIDATE_FUEL_LEVEL_AND_KMS]: "VALIDATE_FUEL_LEVEL_AND_KMS",
  [STEPS.CHECKLIST]: "CHECKLIST",
  [STEPS.VEHICLE_DAMAGE_REPORT_STEP_1]: "VEHICLE_DAMAGE_REPORT_STEP_1",
  [STEPS.VEHICLE_DAMAGE_REPORT_STEP_2]: "VEHICLE_DAMAGE_REPORT_STEP_2",
  [STEPS.KEY_DROP_OFF]: "KEY_DROP_OFF",
  [STEPS.READ_QR_CODE]: "READ_QR_CODE",
  [STEPS.FAIL_QR_CODE]: "FAIL_QR_CODE",
  [STEPS.FINISHED_RENTAL]: "DONE",
};

const STORAGE_KEY = "keyless_damageData";

function loadDamageData() {
  try {
    const saved = sessionStorage.getItem(STORAGE_KEY);
    return saved ? JSON.parse(saved) : {};
  } catch {
    return {};
  }
}

export function useKeylessReturn(callback, deps) {
  const query = useUrlQuery();
  const keylessToken = query ? query.get("token") : null;

  const [loading, setLoading] = useState(true);
  const [currentStep, innerSetCurrentStep] = useState(STEPS.START);
  const [keylessSession, setKeylessSession] = useState(null);
  const [damageData, setDamageData] = useState(loadDamageData);
  const [damageDataLoaded, setDamageDataLoaded] = useState(false);
  const [selectedPart, setSelectedPart] = useState(null);
  const [currentPhotos, setCurrentPhotos] = useState([]);
  const [currentNotes, setCurrentNotes] = useState("");
  const [clientFuelLevel, setClientFuelLevel] = useState(6);
  const [systemFuelLevel, setSystemFuelLevel] = useState(null);
  const [clientKms, setClientKms] = useState(null);
  const [systemKms, setSystemKms] = useState(null);
  // Snapshot do que divergiu no momento da validação, para o ecrã de validação poder
  // mostrar a mensagem certa sem mudar enquanto o cliente corrige os valores.
  const [divergences, setDivergences] = useState({fuel: false, kms: false});
  const [submitting, setSubmitting] = useState(false);
  const [warningModal, setWarningModal] = useState({
    isOpen: false,
    title: "",
    message: "",
  });
  const [selfcheckinPhotos, setSelfcheckinPhotos] = useState([]);
  const [hasDivergence, setHasDivergence] = useState(false);
  const [manualPlate, setManualPlate] = useState("");
  const [validationAttempts, setValidationAttempts] = useState(0);
  const [validatingVehicle, setValidatingVehicle] = useState(false);
  const [vehicleInvalid, setVehicleInvalid] = useState(false);
  const [checklistChecks, setChecklistChecks] = useState({
    belongings: false,
    windows: false,
    keys: false,
  });
  const step4Ref = useRef(null);

  const setCurrentStep = useCallback(
    (step) => {
      innerSetCurrentStep(step);
      const dbStep = STEP_TO_DB[step];
      if (dbStep && keylessToken) {
        updateReturnStep(keylessToken, dbStep).catch(console.error);
      }
    },
    [keylessToken],
  );

  useEffect(() => {
    setLoading(true);
    getReturnKeylessSession(keylessToken)
      .then((res) => {
        setKeylessSession(res);
        if (res.step && STEP_FROM_DB[res.step]) {
          innerSetCurrentStep(STEP_FROM_DB[res.step]);
        }
        // Recupera as leituras já gravadas na sessão para um refresh a meio do processo
        // não apagar os valores dos cartões (nem deixar o pré-fecho sem valor de sistema).
        // O valor do cliente só existe depois do pré-fecho; até lá vale a referência.
        if (res.systemFuelLevel !== null && res.systemFuelLevel !== undefined) {
          setSystemFuelLevel(res.systemFuelLevel);
          setClientFuelLevel(
            res.clientFuelLevel !== null && res.clientFuelLevel !== undefined
              ? res.clientFuelLevel
              : res.systemFuelLevel,
          );
        }
        if (res.systemKms !== null && res.systemKms !== undefined) {
          setSystemKms(res.systemKms);
          setClientKms(
            res.clientKms !== null && res.clientKms !== undefined
              ? res.clientKms
              : res.systemKms,
          );
        }
        setLoading(false);
      })
      .catch((err) => {
        // consoante erro, se for 403 pode fazer redirect para /
        AlertModalManager.openModal(
          { title: "keylessReturnError", message: "keylessSessionError" },
          () => {},
        );
      });
  }, [keylessToken]);

  useEffect(() => {
    sessionStorage.setItem(STORAGE_KEY, JSON.stringify(damageData));
  }, [damageData]);

  useEffect(() => {
    if (
      currentStep === STEPS.VEHICLE_DAMAGE_REPORT_STEP_1 &&
      !damageDataLoaded &&
      keylessToken
    ) {
      getDamageReport(keylessToken)
        .then((response) => {
          if (response.parts && response.parts.length > 0) {
            const loaded = {};
            response.parts.forEach((part) => {
              loaded[part.part] = {
                notes: part.notes || "",
                photos: part.photos.map((p) => ({ id: p.id, url: p.url })),
              };
            });
            setDamageData((prev) => ({ ...loaded, ...prev }));
          }
          setDamageDataLoaded(true);
        })
        .catch(() => {
          setDamageDataLoaded(true);
        });
    }
  }, [currentStep, damageDataLoaded, keylessToken]);

  // Efeito do RENTAL_FINAL_STAGE
  useEffect(() => {
    if (currentStep === STEPS.RENTAL_FINAL_STAGE) {
      if (hasDivergence) {
        setCurrentStep(STEPS.WARNING_RENTAL);
      } else {
        preCloseRA(
          keylessToken,
          systemFuelLevel,
          clientFuelLevel,
          systemKms,
          clientKms,
        );
        setCurrentStep(STEPS.FINISHED_RENTAL);
      }
    }
  }, [
    currentStep,
    clientFuelLevel,
    clientKms,
    hasDivergence,
    keylessToken,
    setCurrentStep,
    systemFuelLevel,
    systemKms,
  ]);

  // Só se compara kms quando a telemática deu leitura de referência: sem leitura não há
  // divergência possível e o valor registado pelo cliente é o único que existe.
  const hasKmsDivergence = systemKms !== null && clientKms !== systemKms;

  const hasFuelLevelDivergence = systemFuelLevel !== clientFuelLevel

  // Os kms são obrigatórios no pré-fecho, por isso não se avança sem valor registado
  // (só acontece quando a telemática não deu leitura e o cliente ainda não editou).
  const hasKmsRegistered = clientKms !== null && clientKms !== undefined;

  const isChecklistComplete =
    checklistChecks.belongings &&
    checklistChecks.windows &&
    checklistChecks.keys;

  // Handlers de danos

  const handleAddDamage = useCallback(
    (part) => {
      setSelectedPart(part);
      setCurrentPhotos(damageData[part]?.photos || []);
      setCurrentNotes(damageData[part]?.notes || "");
      setCurrentStep(STEPS.VEHICLE_DAMAGE_REPORT_STEP_3);
    },
    [damageData, setCurrentStep],
  );

  const handleRegisterDamage = useCallback(() => {
    if (currentPhotos.length === 0) {
      setWarningModal({
        isOpen: true,
        title: "keylessReturnPhotoRequired",
        message: "",
      });
      return;
    }
    setDamageData((prev) => ({
      ...prev,
      [selectedPart]: {
        photos: currentPhotos,
        notes: currentNotes,
      },
    }));
    setCurrentStep(STEPS.VEHICLE_DAMAGE_REPORT_STEP_2);
  }, [selectedPart, currentPhotos, currentNotes, setCurrentStep]);

  const handleCapturePhoto = useCallback(
    async (dataUrl) => {
      const compressed = await compressPhoto(dataUrl);
      setCurrentPhotos((prev) => [...prev, { dataUrl: compressed }]);
      setCurrentStep(STEPS.VEHICLE_DAMAGE_REPORT_STEP_3);
    },
    [setCurrentStep],
  );

  const handleRemovePhoto = useCallback(
    async (index) => {
      const photo = currentPhotos[index];
      // Se a foto já está salva no backend, apagar via API
      if (photo.id) {
        try {
          await deleteDamagePhoto(keylessToken, photo.id);
        } catch (error) {
          console.error("Erro ao apagar foto", error);
          return;
        }
      }
      const updatedPhotos = currentPhotos.filter((_, i) => i !== index);
      setCurrentPhotos(updatedPhotos);
      // Se não restam fotos, limpar a entrada do damageData para esta parte
      if (updatedPhotos.length === 0 && selectedPart) {
        setDamageData((prev) => {
          const updated = { ...prev };
          delete updated[selectedPart];
          return updated;
        });
      }
    },
    [currentPhotos, keylessToken, selectedPart],
  );

  const handleSubmitDamageReport = useCallback(async () => {
    const hasDamages = Object.values(damageData).some(
      (d) => d.photos?.length > 0,
    );
    // Verificar se há fotos novas para enviar
    const hasNewPhotos = Object.values(damageData).some((d) =>
      d.photos?.some((p) => !p.id),
    );

    if (hasDamages || hasFuelLevelDivergence || hasKmsDivergence) {
      setHasDivergence(true);
    }

    if (!hasDamages && selfcheckinPhotos.length === 0) {
      setCurrentStep(STEPS.SELFCHECKIN_CAMERA);
      return;
    }

    if (!hasNewPhotos) {
      // Todas as fotos já estão no backend, só avança
      setCurrentStep(STEPS.KEY_DROP_OFF);
      return;
    }

    setSubmitting(true);
    try {
      const response = await submitDamageReport(keylessToken, damageData);
      // Atualizar damageData com os IDs retornados do backend
      if (response.parts) {
        setDamageData((prev) => {
          const updated = { ...prev };
          response.parts.forEach((part) => {
            const existing = updated[part.part];
            if (existing) {
              // Manter fotos salvas anteriores + adicionar novas com IDs
              const savedPhotos = existing.photos.filter((p) => p.id);
              updated[part.part] = {
                notes: part.notes,
                photos: [...savedPhotos, ...part.photos],
              };
            }
          });
          return updated;
        });
      }
      setCurrentStep(STEPS.KEY_DROP_OFF);
    } catch (error) {
      console.error("Erro ao submeter registo de danos", error);
      // TODO: mostrar modal de erro
    } finally {
      setSubmitting(false);
    }
    // hasFuelLevelDivergence/hasKmsDivergence e selfcheckinPhotos entram nas dependências
    // para a deteção de divergência não correr com valores do primeiro render.
  }, [
    damageData,
    keylessToken,
    setCurrentStep,
    hasFuelLevelDivergence,
    hasKmsDivergence,
    selfcheckinPhotos,
  ]);

  // Vehicle validation — used by both QR scan and manual plate steps
  const MAX_VALIDATION_ATTEMPTS = 3;

  const handleValidateVehicle = useCallback(
    async (qrCode) => {
      setValidatingVehicle(true);
      try {
        await validateReturnKeylessVehicleQRCode(keylessToken, qrCode);
        setCurrentStep(STEPS.RENTAL_FINAL_STAGE);
      } catch {
        const attempts = validationAttempts + 1;
        setValidationAttempts(attempts);
        if (attempts >= MAX_VALIDATION_ATTEMPTS) {
          setVehicleInvalid(true);
        } else {
          setWarningModal({
            isOpen: true,
            title: "keylessReturnInvalidPlate",
            message: "keylessReturnInvalidPlateMsg",
          });
          setCurrentStep(STEPS.FAIL_QR_CODE);
        }
      } finally {
        setValidatingVehicle(false);
      }
    },
    [keylessToken, validationAttempts, setCurrentStep],
  );

  const handleStartKeylessReturn = useCallback(async () => {
    setLoading(true);
    setTimeout(async () => {
      try {
        if (currentStep === STEPS.START) {
          const isInDropOffStation =
            await validateKeylessDropOffLocation(keylessToken);
          if (isInDropOffStation) {
            const {fuelLevel, kms} =
              await keylessCheckFuelLevelAndKms(keylessToken);
            setSystemFuelLevel(fuelLevel);
            // Pré-preenche os valores do cliente com as leituras de referência.
            // Se não vier valor (null), mantém-se o default do useState.
            if (fuelLevel !== null && fuelLevel !== undefined) {
              setClientFuelLevel(fuelLevel);
            }
            if (kms !== null && kms !== undefined) {
              setSystemKms(kms);
              setClientKms(kms);
            }
            setCurrentStep(STEPS.VEHICLE_LOCATION);
          }
        }
      } catch (error) {
        console.error(
          "Erro ao carregar os dados necessários para o processo de devolução keyless",
          error,
        );
      } finally {
        setLoading(false);
      }
    }, 3000);
  }, [currentStep, keylessToken, setCurrentStep]);

  const handleValidateFuelLevelAndKms = useCallback(async () => {
    try {
      const fuelDivergence = clientFuelLevel !== systemFuelLevel;
      setDivergences({fuel: fuelDivergence, kms: hasKmsDivergence});

      fuelDivergence || hasKmsDivergence
        ? setCurrentStep(STEPS.VALIDATE_FUEL_LEVEL_AND_KMS)
        : setCurrentStep(STEPS.CHECKLIST);
    } catch (error) {
      console.error("Erro ao verificar o nível de combustível e os kms", error);
    }
  }, [clientFuelLevel, hasKmsDivergence, setCurrentStep, systemFuelLevel]);

  const handleWarningStep = useCallback(() => {
    if (currentStep !== STEPS.WARNING_RENTAL) return;
    preCloseRA(
      keylessToken,
      systemFuelLevel,
      clientFuelLevel,
      systemKms,
      clientKms,
    );
    setCurrentStep(STEPS.FINISHED_RENTAL);
  }, [
    clientFuelLevel,
    clientKms,
    currentStep,
    keylessToken,
    setCurrentStep,
    systemFuelLevel,
    systemKms,
  ]);

  const handleParkDirections = useCallback(() => {
    if (currentStep !== STEPS.PARK_DIRECTIONS) return;
    window.open(keylessSession.googleMapsUrl, "_blank");
  }, [currentStep]);

  const handleCaptureSelfcheckinPhoto = useCallback(async (dataUrl) => {
    try {
      const compressed = await compressPhoto(dataUrl);
      const response = await submitSelfcheckinPhoto(keylessToken, compressed);
      if (response.photos && response.photos.length > 0) {
        setSelfcheckinPhotos((prev) => [...prev, ...response.photos]);
      }
      setCurrentStep(STEPS.SELFCHECKIN_PHOTO);
    } catch (error) {
      console.error("Erro ao guardar foto de selfcheckin", error);
    }
  }, [keylessToken, setCurrentStep]);

  const handleRemoveSelfcheckinPhoto = useCallback(async (photoId) => {
    try {
      await deleteSelfcheckinPhoto(keylessToken, photoId);
      setSelfcheckinPhotos((prev) => prev.filter((p) => p.id !== photoId));
    } catch (error) {
      console.error("Erro ao apagar foto de selfcheckin", error);
    }
  }, [keylessToken]);

  const handleSubmitSelfcheckin = useCallback(() => {
    if (selfcheckinPhotos.length === 0) {
      setWarningModal({
        isOpen: true,
        title: "keylessReturnPhotoRequired",
        message: "keylessReturnPhotoRequiredMsg",
      });
      return;
    }
    setCurrentStep(STEPS.KEY_DROP_OFF);
  }, [selfcheckinPhotos, setCurrentStep]);

  // Load existing selfcheckin photos on init
  useEffect(() => {
    if (keylessToken) {
      getSelfcheckinPhotos(keylessToken)
        .then((res) => {
          if (res.photos) setSelfcheckinPhotos(res.photos);
        })
        .catch(console.error);
    }
  }, [keylessToken]);

  return {
    // State
    loading,
    currentStep,
    setCurrentStep,
    keylessToken,
    keylessSession,
    setKeylessSession,
    damageData,
    selectedPart,
    currentPhotos,
    currentNotes,
    setCurrentNotes,
    fuelLevel: clientFuelLevel,
    setFuelLevel: setClientFuelLevel,
    kms: clientKms,
    setKms: setClientKms,
    hasKmsRegistered,
    divergences,
    checklistChecks,
    setChecklistChecks,
    isChecklistComplete,
    step4Ref,
    submitting,
    warningModal,
    setWarningModal,
    manualPlate,
    setManualPlate,
    validatingVehicle,
    vehicleInvalid,
    hasDivergence,
    setHasDivergence,

    // Selfcheckin
    selfcheckinPhotos,

    // Handlers
    handleAddDamage,
    handleRegisterDamage,
    handleCapturePhoto,
    handleRemovePhoto,
    handleSubmitDamageReport,
    handleValidateVehicle,
    handleValidateFuelLevelAndKms,
    handleStartKeylessReturn,
    handleWarningStep,
    handleParkDirections,
    handleCaptureSelfcheckinPhoto,
    handleRemoveSelfcheckinPhoto,
    handleSubmitSelfcheckin,
  };
}
